import { useFormik } from "formik"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { array, mixed, object, string } from "yup"

import { ProjectFormPageProps } from "components/ProjectForm"
import { EProjectFormPages } from "components/ProjectForm/config"
import { setLandDealForm } from "components/ProjectForm/ProjectFormSlice"
import { AVAILABLE_CONNECTION_TYPE_OPTIONS, TYPE_OF_PROPERTY_OPTIONS, VEHICLE_TYPE_OPTIONS } from "enums/dropdownValues"
import { EFileTypes } from "enums/fileTypeEnums"
import { useLazyGetAccountDetailsQuery } from "pages/Accounts/AccountsServices"
import { useGetAllAccountsListQuery } from "pages/Common/commonService"
import { DropdownObj } from "pages/Common/commonTypes"
import { SubmitFileInfo, useSubmitFinalUploadsMutation } from "pages/Common/fileUploadService"
import {
    ILandDetailsPageData,
    IProjectFormDocument,
    useLazyGetProjectFormDetailsByStepQuery,
    useUpdateTicketPageDataMutation,
} from "pages/Projects/ProjectServices"
import { TStore } from "store/Store"

export enum ELandDealFields {
    HOST_ACCOUNT = "host_account",
    PROPERTY_TYPE = "property_type",
    CONNECTION_TYPE = "connection_type",
    SANCTIONED_LOAD = "sanctioned_load",
    DG_CONNECTION_USED = "dg_connection_used",
    DG_UNIT_RATE = "dg_unit_rate",
    PARKING_SLOTS = "parking_slots",
    PARKING_TYPE = "parking_type",
    VEHICLE_MOVEMENT = "vehicle_movement",
    DEAL_AMOUNT = "deal_amount",
    LICENSE_FEE_AC = "license_fee_ac",
    LICENSE_FEE_DC = "license_fee_dc",
    INCREMENTAL = "incremental",
    INCREMENTAL_PERCENT = "incremental_percent",
    INCREMENTAL_DURATION = "incremental_duration",
    LAND_SIZE = "land_size",
}

export type LandDealFormValues = {
    [ELandDealFields.HOST_ACCOUNT]: DropdownObj | null
    [ELandDealFields.PROPERTY_TYPE]: { id: string; title: string } | null
    [ELandDealFields.CONNECTION_TYPE]: { id: string; title: string } | null
    [ELandDealFields.SANCTIONED_LOAD]: string
    [ELandDealFields.DG_CONNECTION_USED]: DropdownObj | null
    [ELandDealFields.DG_UNIT_RATE]: string
    [ELandDealFields.PARKING_SLOTS]: string
    [ELandDealFields.PARKING_TYPE]: DropdownObj | null
    [ELandDealFields.VEHICLE_MOVEMENT]: DropdownObj[]
    [ELandDealFields.DEAL_AMOUNT]: string
    [ELandDealFields.LICENSE_FEE_AC]: string
    [ELandDealFields.LICENSE_FEE_DC]: string
    [ELandDealFields.INCREMENTAL]: DropdownObj | null
    [ELandDealFields.INCREMENTAL_PERCENT]: string
    [ELandDealFields.INCREMENTAL_DURATION]: DropdownObj | null
    [ELandDealFields.LAND_SIZE]: string | null
}

const initialValues: LandDealFormValues = {
    [ELandDealFields.HOST_ACCOUNT]: null,
    [ELandDealFields.PROPERTY_TYPE]: { id: "rented", title: "Rented" },
    [ELandDealFields.CONNECTION_TYPE]: { id: "commercial", title: "Commercial" },
    [ELandDealFields.SANCTIONED_LOAD]: "",
    [ELandDealFields.DG_CONNECTION_USED]: { id: 0, title: "No" },
    [ELandDealFields.DG_UNIT_RATE]: "",
    [ELandDealFields.PARKING_SLOTS]: "",
    [ELandDealFields.PARKING_TYPE]: null,
    [ELandDealFields.VEHICLE_MOVEMENT]: [],
    [ELandDealFields.DEAL_AMOUNT]: "",
    [ELandDealFields.LICENSE_FEE_AC]: "",
    [ELandDealFields.LICENSE_FEE_DC]: "",
    [ELandDealFields.INCREMENTAL]: { id: 0, title: "No" },
    [ELandDealFields.INCREMENTAL_PERCENT]: "",
    [ELandDealFields.INCREMENTAL_DURATION]: null,
    [ELandDealFields.LAND_SIZE]: null,
}

const dropdownSchema = object({
    id: mixed().required(),
    title: string().required(),
})

const optionalDropdownSchema = dropdownSchema.nullable().default(null)
const requiredDropdownSchema = dropdownSchema.nullable().required("This field is required")

const validationSchema = object({
    [ELandDealFields.HOST_ACCOUNT]: requiredDropdownSchema,
    [ELandDealFields.PROPERTY_TYPE]: requiredDropdownSchema,
    [ELandDealFields.CONNECTION_TYPE]: requiredDropdownSchema,
    [ELandDealFields.SANCTIONED_LOAD]: string().trim().default(""),
    [ELandDealFields.DG_CONNECTION_USED]: requiredDropdownSchema,
    [ELandDealFields.DG_UNIT_RATE]: string()
        .trim()
        .default("")
        .when(ELandDealFields.DG_CONNECTION_USED, {
            is: (dgConnection: DropdownObj | null) => dgConnection?.id === 1,
            then: schema => schema.required("DG Unit Rate is required"),
            otherwise: schema => schema,
        }),
    [ELandDealFields.PARKING_SLOTS]: string().trim().default(""),
    [ELandDealFields.PARKING_TYPE]: requiredDropdownSchema,
    [ELandDealFields.VEHICLE_MOVEMENT]: array().of(dropdownSchema).default([]),
    [ELandDealFields.DEAL_AMOUNT]: string().trim().default(""),
    [ELandDealFields.LICENSE_FEE_AC]: string().trim().default(""),
    [ELandDealFields.LICENSE_FEE_DC]: string().trim().default(""),
    [ELandDealFields.INCREMENTAL]: optionalDropdownSchema,
    [ELandDealFields.INCREMENTAL_PERCENT]: string()
        .trim()
        .default("")
        .when([ELandDealFields.INCREMENTAL], {
            is: (incremental: DropdownObj | null) => incremental?.id === 1,
            then: schema => schema.required("Incremental % is required"),
            otherwise: schema => schema,
        }),
    [ELandDealFields.INCREMENTAL_DURATION]: dropdownSchema
        .nullable()
        .default(null)
        .when([ELandDealFields.INCREMENTAL], {
            is: (incremental: DropdownObj | null) => incremental?.id === 1,
            then: schema => schema.required("Incremental Duration is required"),
            otherwise: schema => schema.nullable(),
        }),
})

const FILE_TYPES = {
    UPLOAD_BILL: EFileTypes.ELECTRICITY_BILL,
    HOST_AGREEMENT: EFileTypes.RENT_AGREEMENT,
    NOC: EFileTypes.NOC_HOST,
    PROPERTY_OWNERSHIP: EFileTypes.PROPERTY_PAPER,
}

const useLandDeal = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const { project_id, land_deal_form } = useSelector((store: TStore) => store.ProjectForm)
    const [updateTicketPageData, { isLoading: isUpdatingPageData }] = useUpdateTicketPageDataMutation()
    const [submitFinalUploads, { isLoading: isSubmittingFinalUploads }] = useSubmitFinalUploadsMutation()
    const [getProjectFormDetailsByStep] = useLazyGetProjectFormDetailsByStepQuery()
    const [getAccountDetails] = useLazyGetAccountDetailsQuery()
    const [destinationSiteId, setDestinationSiteId] = useState<number | null>(null)
    const pendingFileInfosRef = useRef<SubmitFileInfo[]>([])
    const hasPrefilled = useRef(false)

    const [existingBillDocuments, setExistingBillDocuments] = useState<IProjectFormDocument[]>([])
    const [existingHostAgreementDocuments, setExistingHostAgreementDocuments] = useState<IProjectFormDocument[]>([])
    const [existingNocDocuments, setExistingNocDocuments] = useState<IProjectFormDocument[]>([])
    const [existingPropertyOwnershipDocuments, setExistingPropertyOwnershipDocuments] = useState<
        IProjectFormDocument[]
    >([])

    const [fileErrors, setFileErrors] = useState<Record<string, boolean>>({})
    const uploadedFileTypesRef = useRef<Set<string>>(new Set())

    const handleFileUpload = (fileInfos: SubmitFileInfo[], fileType?: string, previousFileInfos?: SubmitFileInfo[]) => {
        const prevDocIds = new Set(previousFileInfos?.map(f => f.doc_id))
        pendingFileInfosRef.current = [
            ...pendingFileInfosRef.current.filter(f => !prevDocIds.has(f.doc_id)),
            ...fileInfos,
        ]
        if (fileType) {
            uploadedFileTypesRef.current.add(fileType)
            setFileErrors(prev => ({ ...prev, [fileType]: false }))
        }
    }

    const submitPendingFiles = async () => {
        if (pendingFileInfosRef.current.length === 0 || !project_id) return
        try {
            await submitFinalUploads({
                ticket_id: project_id,
                file_infos: pendingFileInfosRef.current,
            }).unwrap()
            pendingFileInfosRef.current = []
        } catch (error) {
            console.error("Error submitting final uploads:", error)
        }
    }

    const { data: allAccountsList } = useGetAllAccountsListQuery()

    const accountOptions = useMemo(() => {
        return (
            allAccountsList?.map(account => ({
                id: account.id,
                title: account.account_name,
            })) || []
        )
    }, [allAccountsList])

    const parkingTypeOptions = [
        { id: 1, title: "Surface" },
        { id: 2, title: "Basement" },
        { id: 3, title: "Multi-Level" },
    ]

    const incrementalDurationOptions = [
        { id: 6, title: "6 Months" },
        { id: 12, title: "12 Months" },
        { id: 18, title: "18 Months" },
        { id: 24, title: "24 Months" },
    ]

    const formik = useFormik<LandDealFormValues>({
        initialValues: land_deal_form ? { ...land_deal_form } : initialValues,
        validationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const fetchAndPrefillLandDealData = useCallback(async () => {
        if (!props.isEditMode || !props.projectId || hasPrefilled.current) return
        if (accountOptions.length === 0) return

        try {
            const response = await getProjectFormDetailsByStep({
                project_id: props.projectId,
                page_title: EProjectFormPages.LAND_DETAILS,
            }).unwrap()

            const documents = (response?.documents || []) as IProjectFormDocument[]
            const pageFormData = response?.pages?.[EProjectFormPages.LAND_DETAILS]
            if (!pageFormData) return

            hasPrefilled.current = true
            setDestinationSiteId(pageFormData.site_id?.[0])
            const billDocs = documents.filter(doc => doc.document_type === FILE_TYPES.UPLOAD_BILL)
            setExistingBillDocuments(billDocs)

            const hostAgreementDocs = documents.filter(doc => doc.document_type === FILE_TYPES.HOST_AGREEMENT)
            setExistingHostAgreementDocuments(hostAgreementDocs)

            const nocDocs = documents.filter(doc => doc.document_type === FILE_TYPES.NOC)
            setExistingNocDocuments(nocDocs)

            const propertyOwnershipDocs = documents.filter(doc => doc.document_type === FILE_TYPES.PROPERTY_OWNERSHIP)
            setExistingPropertyOwnershipDocuments(propertyOwnershipDocs)
            const data = pageFormData.page_data || pageFormData

            if (data.account_id) {
                const matched = accountOptions.find(acc => acc.id === data.account_id)
                if (matched) {
                    formik.setFieldValue(ELandDealFields.HOST_ACCOUNT, matched)
                } else {
                    // The host account is read-only here, so it may sit outside the loaded
                    // accounts list. Resolve its name directly so prefill still shows it.
                    try {
                        const accountData = await getAccountDetails(Number(data.account_id)).unwrap()
                        if (accountData?.account_name) {
                            formik.setFieldValue(ELandDealFields.HOST_ACCOUNT, {
                                id: data.account_id,
                                title: accountData.account_name,
                            })
                        }
                    } catch (error) {
                        console.error("Error resolving host account name:", error)
                    }
                }
            }
            if (data.property_type) {
                const value = TYPE_OF_PROPERTY_OPTIONS.find(i => i.id === data.property_type)
                if (value) formik.setFieldValue(ELandDealFields.PROPERTY_TYPE, value)
            }
            if (data.connection_type) {
                const value = AVAILABLE_CONNECTION_TYPE_OPTIONS.find(o => o.id === data.connection_type)
                if (value) formik.setFieldValue(ELandDealFields.CONNECTION_TYPE, value)
            }
            if (data.sanctioned_load != null)
                formik.setFieldValue(ELandDealFields.SANCTIONED_LOAD, String(data.sanctioned_load))
            if (data.dg_connection_used != null) {
                formik.setFieldValue(
                    ELandDealFields.DG_CONNECTION_USED,
                    data.dg_connection_used ? { id: 1, title: "Yes" } : { id: 0, title: "No" }
                )
            }
            if (data.dg_unit_rate != null) formik.setFieldValue(ELandDealFields.DG_UNIT_RATE, String(data.dg_unit_rate))
            if (data.parking_slots != null)
                formik.setFieldValue(ELandDealFields.PARKING_SLOTS, String(data.parking_slots))
            if (data.parking_type) {
                const matched = parkingTypeOptions.find(o => o.title.toLowerCase() === data.parking_type.toLowerCase())
                if (matched) formik.setFieldValue(ELandDealFields.PARKING_TYPE, matched)
            }
            if (data.deal_amount != null) formik.setFieldValue(ELandDealFields.DEAL_AMOUNT, String(data.deal_amount))
            if (data.license_fee_ac != null)
                formik.setFieldValue(ELandDealFields.LICENSE_FEE_AC, String(data.license_fee_ac))
            if (data.license_fee_dc != null)
                formik.setFieldValue(ELandDealFields.LICENSE_FEE_DC, String(data.license_fee_dc))
            if (data.incremental != null) {
                formik.setFieldValue(
                    ELandDealFields.INCREMENTAL,
                    data.incremental ? { id: 1, title: "Yes" } : { id: 0, title: "No" }
                )
            }
            if (data.incremental_percent != null)
                formik.setFieldValue(ELandDealFields.INCREMENTAL_PERCENT, String(data.incremental_percent))
            if (data.incremental_duration) {
                const matched = incrementalDurationOptions.find(o => o.id === data.incremental_duration)
                if (matched) formik.setFieldValue(ELandDealFields.INCREMENTAL_DURATION, matched)
            }
            if (data.land_size) {
                formik.setFieldValue(ELandDealFields.LAND_SIZE, data.land_size)
            }
            if (data.vehicle_movement?.length) {
                const vehicleMovementValues: DropdownObj[] = data.vehicle_movement.map((v: string) => {
                    const matched = VEHICLE_TYPE_OPTIONS.find(
                        o =>
                            o.title.toLowerCase().includes(v.toLowerCase()) ||
                            v.toLowerCase().includes(o.title.toLowerCase())
                    )
                    return matched || null
                })
                formik.setFieldValue(ELandDealFields.VEHICLE_MOVEMENT, vehicleMovementValues)
            }
        } catch (error) {
            console.error("Error fetching land deal prefill data:", error)
        }
    }, [props.isEditMode, props.projectId, accountOptions, getProjectFormDetailsByStep, getAccountDetails, formik])

    useEffect(() => {
        fetchAndPrefillLandDealData()
    }, [fetchAndPrefillLandDealData])

    function hasFileError(fileType: string) {
        return Boolean(fileErrors[fileType])
    }

    async function handleSubmit(values: LandDealFormValues) {
        const mandatoryFileTypes = [FILE_TYPES.UPLOAD_BILL, FILE_TYPES.HOST_AGREEMENT, FILE_TYPES.NOC]

        const newFileErrors: Record<string, boolean> = {}
        let hasAnyFileError = false

        for (const ft of mandatoryFileTypes) {
            const hasExisting =
                (ft === FILE_TYPES.UPLOAD_BILL && existingBillDocuments.length > 0) ||
                (ft === FILE_TYPES.HOST_AGREEMENT && existingHostAgreementDocuments.length > 0) ||
                (ft === FILE_TYPES.NOC && existingNocDocuments.length > 0)
            const hasNewUpload = uploadedFileTypesRef.current.has(ft)

            if (!hasExisting && !hasNewUpload) {
                newFileErrors[ft] = true
                hasAnyFileError = true
            }
        }

        if (hasAnyFileError) {
            setFileErrors(newFileErrors)
            enqueueSnackbar("Please upload all mandatory files", { variant: "error" })
            return
        }
        const payload: ILandDetailsPageData = {
            page_title: (props.pageData?.title as EProjectFormPages.LAND_DETAILS) || EProjectFormPages.LAND_DETAILS,
            project_id: props.projectId!,
            site_id: destinationSiteId!,
            account_id: values[ELandDealFields.HOST_ACCOUNT]?.id || 0,
            property_type: values[ELandDealFields.PROPERTY_TYPE]?.id || "",
            connection_type: values[ELandDealFields.CONNECTION_TYPE]?.id || "",
            sanctioned_load: Number(values[ELandDealFields.SANCTIONED_LOAD]) || 0,
            dg_connection_used: values[ELandDealFields.DG_CONNECTION_USED]?.id === 1 ? 1 : 0,
            dg_unit_rate: Number(values[ELandDealFields.DG_UNIT_RATE]) || 0,
            parking_slots: Number(values[ELandDealFields.PARKING_SLOTS]) || 0,
            parking_type: values[ELandDealFields.PARKING_TYPE]?.title || "",
            vehicle_movement: values[ELandDealFields.VEHICLE_MOVEMENT].map(v => v.title.toLowerCase()),
            deal_amount: Number(values[ELandDealFields.DEAL_AMOUNT]) || 0,
            license_fee_ac: Number(values[ELandDealFields.LICENSE_FEE_AC]) || 0,
            license_fee_dc: Number(values[ELandDealFields.LICENSE_FEE_DC]) || 0,
            incremental: values[ELandDealFields.INCREMENTAL]?.id === 1 ? 1 : 0,
            ...(values[ELandDealFields.INCREMENTAL]?.id === 1 && {
                incremental_percent: Number(values[ELandDealFields.INCREMENTAL_PERCENT]) || 0,
                incremental_duration: values[ELandDealFields.INCREMENTAL_DURATION]?.id || 0,
            }),
            land_size: values[ELandDealFields.LAND_SIZE] ? Number(values[ELandDealFields.LAND_SIZE]) : null,
        }

        try {
            await updateTicketPageData(payload).unwrap()

            await submitPendingFiles()

            dispatch(setLandDealForm(values))

            enqueueSnackbar("Land details saved successfully", { variant: "success" })
            props.handleNextStep()
        } catch (error) {
            console.error("Error updating land deal page data:", error)
            enqueueSnackbar("Error saving land details", { variant: "error" })
        }
    }

    function hasError(field: ELandDealFields) {
        return Boolean(formik.submitCount > 0 && formik.errors[field])
    }
    const isIncrementalYes = formik.values[ELandDealFields.INCREMENTAL]?.id === 1
    const isDgConnectionUsed = formik.values[ELandDealFields.DG_CONNECTION_USED]?.id === 1

    return {
        states: {
            accountOptions,
            parkingTypeOptions,
            incrementalDurationOptions,
            formik,
            isIncrementalYes,
            isDgConnectionUsed,
            isUpdatingPageData,
            project_id: project_id ?? undefined,
            FILE_TYPES,
            existingBillDocuments,
            existingHostAgreementDocuments,
            existingNocDocuments,
            existingPropertyOwnershipDocuments,
            isSubmitting: isUpdatingPageData || isSubmittingFinalUploads,
        },
        functions: {
            handleSubmit,
            hasError,
            hasFileError,
            handleFileUpload,
        },
    }
}

export default useLandDeal
